import { useState } from 'react'
import useSWR from 'swr'
import { API_BASE_URL } from 'config/api'
import { smartAuthenticatedFetcher, IError, ApiResponse, Meta } from 'utils/fetchers'

const apiUrl = `${API_BASE_URL}/customer/orders`

interface Product {
  image: string
  title: string
}

interface StatusHistoryType {
  status: string
  changedAt: string | number | Date
}

interface OrderItems {
  discount: number
  id: string
  orderId: string
  ownerId: string
  product: Product
  productId: string
  quantity: number
  shipmentTrackingCode: number | null
  status: string
  statusHistory: StatusHistoryType[]
  subTotal: number
  total: number
  vat: number
}

interface Order {
  createdAt: string
  currency: string
  customerId: string
  discount: number
  id: string
  items: OrderItems[]
  receiverFirstName: string
  receiverLastName: string
  receiverTelephone: string
  shipmentTrackingCode: number | null
  shippingAddressCityId: number
  shippingAddressLatitude: number | null
  shippingAddressLongitude: number | null
  shippingAddressPostalCode: string
  shippingAddressText: string
  shippingPrice: number
  status: string
  statusHistory: StatusHistoryType[]
  subTotal: number
  total: number
  vat: number
}

interface PaginationOptions {
  page?: number
  per_page?: number
}

export function useFetchOrders(paginationOptions?: PaginationOptions) {
  const [page, setPage] = useState(paginationOptions?.page || 1)
  const [per_page, setPerPage] = useState(paginationOptions?.per_page || 20)

  // Use smartAuthenticatedFetcher which handles token refresh automatically
  // No need to include token in cache key - fetcher handles auth internally
  const { data: response, error } = useSWR<ApiResponse<Order[]>, IError | null>(
    `${apiUrl}?page=${page}&per_page=${per_page}`,
    smartAuthenticatedFetcher,
  )

  // Extract data and meta from response
  const orders = response?.data || undefined
  const meta = response?.meta

  // Check for API-level errors
  const apiError = response && !response.success ? response.error : null
  const finalError = error || apiError

  const goToPage = (pageNum: number) => setPage(Math.max(1, pageNum))
  const setItemsPerPage = (count: number) => {
    setPerPage(Math.max(1, count))
    setPage(1) // Reset to first page when changing items per page
  }

  return {
    orders,
    isLoading: !finalError && !response,
    isError: !!finalError,
    error: finalError,
    meta: meta as Meta | undefined,
    pagination: {
      page,
      per_page,
      goToPage,
      setItemsPerPage,
    },
  }
}
